import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { authAxios } from "../../utils/auth";
import AdminMenu from "../../components/AdminMenu";
import CaseDetailPanel from "../../components/admin/CaseDetailPanel";
import { Card } from "../../components/ui/card";
import {
  ArrowLeft, Shield, RefreshCw, Users, MessageSquare,
  Coins, Calendar, Mail, Phone, MapPin, Fingerprint,
} from "lucide-react";
import { toast } from "sonner";

const FRAUD_STATUS_COLOR = {
  clean: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  watch: "bg-white/10 text-white/70 border-white/20",
  prewarn: "bg-amber-500/20 text-amber-300 border-amber-500/40",
  freeze: "bg-red-500/20 text-red-400 border-red-500/40",
  confirmed_fraud: "bg-red-900/40 text-red-300 border-red-700",
};
const ACCOUNT_STATUS_COLOR = {
  active: "text-emerald-300",
  warning: "text-amber-300",
  frozen: "text-red-300",
  blocked: "text-red-200",
};

const LAST_CASE_KEY = (uid) => `fraud_defender_last_case:${uid}`;

/**
 * /admin/fraud/defender/:userId — Fraud eval workspace.
 *
 * One-page workspace that combines the user's profile + usage stats,
 * their full case history (sortable by most-recently-touched), and the
 * inline CaseDetailPanel for the currently-selected case. Deep-linkable
 * with `?case_id=<id>` and remembers the last visited case per user in
 * localStorage.
 */
export default function FraudDefender() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async ({ refresh = false } = {}) => {
    setLoading(true);
    try {
      const r = await authAxios.get(
        `/admin/fraud/defender/user/${userId}${refresh ? "?refresh=true" : ""}`,
      );
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load user");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId]);

  const cases = data?.cases || [];
  const activeCaseId = useMemo(() => {
    const urlCase = searchParams.get("case_id");
    if (urlCase && cases.some((c) => c.case_id === urlCase)) return urlCase;
    const remembered = localStorage.getItem(LAST_CASE_KEY(userId));
    if (remembered && cases.some((c) => c.case_id === remembered)) return remembered;
    return data?.active_case_id || null;
  }, [searchParams, cases, data?.active_case_id, userId]);

  const selectCase = (case_id) => {
    setSearchParams((p) => { p.set("case_id", case_id); return p; });
    try { localStorage.setItem(LAST_CASE_KEY(userId), case_id); } catch { /* ignore */ }
  };

  if (loading && !data) {
    return <div className="min-h-screen bg-divine-black text-white/50 flex items-center justify-center">Loading…</div>;
  }
  if (!data) return null;
  const { user, stats } = data;

  return (
    <div className="min-h-screen bg-divine-black text-white">
      {/* Header */}
      <header className="border-b border-white/10 bg-divine-black-light/70 backdrop-blur sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/5"
              data-testid="fraud-defender-back"
              title="Back"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2 min-w-0">
              <Shield className="w-5 h-5 text-red-400" />
              <div className="min-w-0">
                <h1 className="text-sm font-semibold uppercase tracking-wider">Fraud Defender</h1>
                <p className="text-[11px] text-white/40 font-mono truncate">{user.user_id}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => load({ refresh: true })}
              className="p-2 rounded-lg text-white/50 hover:text-white hover:bg-white/10"
              data-testid="fraud-defender-refresh"
              title="Refresh (busts cache)"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? "animate-spin" : ""}`} />
            </button>
            <AdminMenu />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-4">
        {/* USER STATS CARD */}
        <Card className="bg-[#0f0f0f] border-white/5 p-4" data-testid="fraud-defender-user-card">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="text-lg text-white font-semibold truncate">{user.name || user.email}</h2>
                <span className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-wider border ${FRAUD_STATUS_COLOR[user.fraud_status] || FRAUD_STATUS_COLOR.clean}`}
                      data-testid="user-fraud-status">
                  fraud: {user.fraud_status}
                </span>
                <span className={`text-[10px] uppercase tracking-wider font-semibold ${ACCOUNT_STATUS_COLOR[user.account_status] || "text-white/50"}`}
                      data-testid="user-account-status">
                  status: {user.account_status}
                </span>
                {user.role !== "user" && (
                  <span className="px-2 py-0.5 rounded text-[10px] uppercase tracking-wider bg-divine-gold/15 text-divine-gold">
                    {user.role}
                  </span>
                )}
              </div>
              <Link to={`/admin/users/${user.user_id}`} className="text-[11px] text-divine-gold hover:underline" data-testid="fraud-defender-openuser">
                → Open full user page
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            <UserFact icon={Mail} label="Email" value={user.email} mono />
            <UserFact icon={Phone} label="Phone" value={user.phone_number} />
            <UserFact icon={MapPin} label="Location" value={[user.city, user.state, user.country].filter(Boolean).join(", ") || "—"} />
            <UserFact icon={Calendar} label="Joined" value={user.created_at ? new Date(user.created_at).toLocaleDateString() : "—"} />
            <UserFact icon={Fingerprint} label="Plan" value={user.subscription_tier || "—"} sub={user.subscription_status} />
            <UserFact icon={Users} label="Relationships" value={stats.relationships_count} sub={`${stats.chat_sessions_count} sessions`} />
            <UserFact icon={MessageSquare} label="Chat sessions" value={stats.chat_sessions_count} />
            <UserFact icon={Fingerprint} label="Tokens spent" value={stats.tokens_spent.toLocaleString()} />
            <UserFact icon={Coins} label="Available" value={`$${(stats.commissions.AVAILABLE / 100).toFixed(2)}`} />
            <UserFact icon={Coins} label="Pending" value={`$${(stats.commissions.PENDING / 100).toFixed(2)}`} />
            <UserFact icon={Coins} label="Total earned" value={`$${(stats.commissions.TOTAL_EARNED / 100).toFixed(2)}`} />
            <UserFact icon={Coins} label="Frozen" value={`$${(stats.commissions.FROZEN / 100).toFixed(2)}`} tone={stats.commissions.FROZEN ? "red" : ""} />
          </div>

          {/* Status history */}
          {(user.status_history || []).length > 0 && (
            <details className="mt-4">
              <summary className="text-[10px] uppercase tracking-wider text-white/40 cursor-pointer hover:text-white/60">
                Status history — {user.status_history.length} change{user.status_history.length !== 1 ? "s" : ""}
              </summary>
              <ul className="mt-2 space-y-1 max-h-40 overflow-y-auto" data-testid="fraud-defender-status-history">
                {user.status_history.slice().reverse().map((h, i) => (
                  <li key={i} className="text-[11px] flex items-center gap-2 text-white/70">
                    <span className="font-mono text-white/40">{new Date(h.at).toLocaleString()}</span>
                    <span className="uppercase text-[9px] tracking-wider">
                      {h.from} <span className="text-white/30">→</span> <span className="text-white">{h.to}</span>
                    </span>
                    {h.reason && <span className="text-white/50 truncate">· {h.reason}</span>}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </Card>

        {/* CASES LAYOUT */}
        <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-4">
          <Card className="bg-[#0a0a0a] border-white/5 p-3 lg:sticky lg:top-24 lg:h-fit" data-testid="fraud-defender-case-list">
            <div className="text-[10px] uppercase tracking-wider text-white/40 mb-2 flex items-center justify-between">
              <span>Cases ({cases.length})</span>
              <span className="text-white/30">Most recent</span>
            </div>
            {cases.length === 0 ? (
              <div className="text-xs text-white/40 py-6 text-center">No cases logged.</div>
            ) : (
              <ul className="space-y-1">
                {cases.map((c) => {
                  const active = c.case_id === activeCaseId;
                  const touched = c.updated_at || c.created_at;
                  return (
                    <li key={c.case_id}>
                      <button
                        onClick={() => selectCase(c.case_id)}
                        className={`w-full text-left px-2 py-2 rounded border transition-colors ${
                          active
                            ? "bg-divine-gold/10 border-divine-gold/40 text-white"
                            : "bg-transparent border-white/5 hover:bg-white/[0.03] hover:border-white/15 text-white/80"
                        }`}
                        data-testid={`case-list-${c.case_id}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className={`px-1.5 py-0.5 text-[9px] uppercase tracking-wider rounded border ${FRAUD_STATUS_COLOR[c.status] || FRAUD_STATUS_COLOR.watch}`}>
                            {c.status}
                          </span>
                          <span className="text-[9px] text-white/30 font-mono">
                            {touched ? new Date(touched).toLocaleDateString() : "—"}
                          </span>
                        </div>
                        <div className="text-[10px] font-mono text-white/60 mt-1 truncate">{c.case_id}</div>
                        <div className="text-[10px] text-white/40 mt-0.5">
                          risk {c.risk_score || 0} · tier {c.tier || "—"}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>

          <Card className="bg-[#0a0a0a] border-white/5 p-4 min-w-0">
            <CaseDetailPanel caseId={activeCaseId} onUpdated={() => load({ refresh: true })} />
          </Card>
        </div>
      </main>
    </div>
  );
}

function UserFact({ icon: Icon, label, value, sub, mono, tone }) {
  const toneClass = tone === "red" ? "text-red-300" : "text-white/90";
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-1 text-white/40 text-[10px] uppercase tracking-wider">
        {Icon && <Icon className="w-3 h-3" />}
        {label}
      </div>
      <div className={`mt-0.5 text-sm truncate ${mono ? "font-mono" : ""} ${toneClass}`}>{value ?? "—"}</div>
      {sub && <div className="text-[10px] text-white/40 truncate">{sub}</div>}
    </div>
  );
}
